import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../components/UI/Button';
import {
  ExclamationTriangleIcon,
  HomeIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const NotFound = () => {
  const location = useLocation();

  const suggestions = [
    {
      title: 'Bridge Tokens',
      description: 'Transfer PFVS tokens between Ronin and BNB Chain',
      path: '/',
      icon: HomeIcon
    },
    {
      title: 'Transaction History',
      description: 'View your past bridge transactions',
      path: '/history',
      icon: MagnifyingGlassIcon
    }
  ];

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-md mx-auto">
        {/* Error Icon */}
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-error-100 dark:bg-error-900/50 rounded-full flex items-center justify-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-error-600 dark:text-error-400" />
          </div>
        </div>

        {/* Error Message */}
        <div className="mb-8">
          <h1 className="text-6xl font-bold text-slate-900 dark:text-slate-100 mb-4">
            404
          </h1>
          <h2 className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Page Not Found
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            The page you're looking for doesn't exist or has been moved.
          </p>
          
          {location.pathname && (
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 mb-6">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Requested path: <code className="text-slate-800 dark:text-slate-200">{location.pathname}</code>
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
          <Button
            as={Link}
            to="/"
            variant="primary"
            size="lg"
            className="flex items-center gap-2"
          >
            <HomeIcon className="w-5 h-5" />
            Go to Bridge
          </Button>
          
          <Button
            onClick={() => window.history.back()}
            variant="secondary"
            size="lg"
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Go Back
          </Button>
        </div>

        {/* Suggestions */}
        <div className="text-left">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 text-center">
            Suggested Pages
          </h3>
          
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <Link
                key={index}
                to={suggestion.path}
                className="block p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group"
              >
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg group-hover:bg-primary-200 dark:group-hover:bg-primary-800/50 transition-colors">
                    <suggestion.icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                      {suggestion.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {suggestion.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            If you believe this is an error or need assistance, please check the URL or contact support.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound; 